import type { CSSProperties, ReactNode } from 'react'
import type { BadgeColor } from '../Badge/Badge.types'
import type { ScheduleCardColor } from '../ScheduleCard/ScheduleCard.types'

export type SchedulerView = 'day' | 'week' | 'month'
export type SchedulerViewMode = 'Fixed' | 'Full'

export type SchedulerApptStatus =
  | 'Unconfirmed'
  | 'Confirmed'
  | 'Late'
  | 'No-show'
  | 'Check-in'
  | 'In-treatment'
  | 'Check-out'
  | 'Complete'

export type SchedulerColumn = {
  id: string
  label: string
}

export type SchedulerAppointmentInfo = {
  patientName: string
  provider?: string
  clinic?: string
  procedure?: string
  dateTime?: string
  status?: string
  statusColor?: BadgeColor
  preferredDentist?: string
  preferredHygienist?: string
  isInsured?: string
  conditions?: string
  note?: string
  preAuthStatus?: string
  preAuthColor?: BadgeColor
}

export type SchedulerEvent = {
  id: string
  columnId: string
  /** Local start datetime. */
  start: Date
  /** Local end datetime. */
  end: Date
  patientName: string
  appointmentType?: string
  color?: ScheduleCardColor
  providerId?: string
  /** Provider display name for Month grouping (falls back to info.provider). */
  providerName?: string
  status?: SchedulerApptStatus
  headerIcons?: ReactNode
  /** Created or updated by the AI receptionist. */
  aiAssisted?: boolean
  /** Hover text for the AI receptionist icon. */
  aiHint?: string
  /** Detail fields for the Appointment Info panel. */
  info?: SchedulerAppointmentInfo
}

/** Horizontal band across the day grid (e.g. lunch / focus slot). */
export type SchedulerHighlightBand = {
  id: string
  /** Hour as decimal (e.g. 9.5 = 9:30). */
  startHour: number
  endHour: number
  /** yellow lunch band, blue focus/now slot. */
  tone?: 'yellow' | 'blue'
  className?: string
}

/** Closed-hour / unavailable band (non-interactive). */
export type SchedulerBlockRange = {
  id: string
  startHour: number
  endHour: number
  /** When set, block applies to one Day column; omit for all columns (Week). */
  columnId?: string
}

export type SchedulerFilterState = {
  providerIds: string[]
  roomIds: string[]
  statuses: SchedulerApptStatus[]
}

export type SchedulerProviderOption = {
  id: string
  name: string
}

/** Empty grid slot selection (e.g. open Set appointment modal). */
export type SchedulerSlotSelection = {
  start: Date
  end: Date
  columnId: string
}

export type SchedulerProps = {
  columns?: SchedulerColumn[]
  events?: SchedulerEvent[]
  date?: Date
  defaultDate?: Date
  view?: SchedulerView
  defaultView?: SchedulerView
  /**
   * Density mode from Settings → System → Scheduler → View mode.
   * Not shown in the toolbar — pass from host/settings.
   */
  viewMode?: SchedulerViewMode
  /**
   * Initial density when `viewMode` is uncontrolled.
   */
  defaultViewMode?: SchedulerViewMode
  /** First hour shown on the Day/Week grid (inclusive). */
  dayStartHour?: number
  /** Last hour shown on the Day/Week grid (exclusive). */
  dayEndHour?: number
  /** Vertical pixels per hour in Fixed mode. day grid uses 64. */
  pixelsPerHour?: number
  highlightBands?: SchedulerHighlightBand[]
  blockRanges?: SchedulerBlockRange[]
  providers?: SchedulerProviderOption[]
  filters?: SchedulerFilterState
  defaultFilters?: SchedulerFilterState
  searchValue?: string
  searchPlaceholder?: string
  showToolbar?: boolean
  /** Enable drag/resize, context menu, move/edit modals (mock interactions). */
  enableInteractions?: boolean
  /**
   * When false, card click only fires `onEventClick` (no built-in detail modal).
   * Use with a host page modal such as ScheduleAppointment.
   */
  showEventDetailModal?: boolean
  /** Dark surface styling. */
  darkMode?: boolean
  className?: string
  style?: CSSProperties
  onDateChange?: (date: Date) => void
  onViewChange?: (view: SchedulerView) => void
  onSearchChange?: (value: string) => void
  onFiltersChange?: (filters: SchedulerFilterState) => void
  onEventsChange?: (events: SchedulerEvent[]) => void
  onTodayClick?: () => void
  onAddPatientClick?: () => void
  onSetAppointmentClick?: () => void
  onEventClick?: (event: SchedulerEvent) => void
  /** Click empty Day/Week grid slot (Appointment Set-appointment flow). */
  onSlotClick?: (slot: SchedulerSlotSelection) => void
  onInfoClick?: () => void
}

export const SCHEDULER_VIEW_ITEMS: { id: SchedulerView; label: string }[] = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
]

export const SCHEDULER_APPT_STATUSES: SchedulerApptStatus[] = [
  'Unconfirmed',
  'Confirmed',
  'Late',
  'No-show',
  'Check-in',
  'In-treatment',
  'Check-out',
  'Complete',
]

export const SCHEDULER_STATUS_BADGE_COLOR: Record<
  SchedulerApptStatus,
  BadgeColor
> = {
  Unconfirmed: 'gray',
  Confirmed: 'pale-green',
  Late: 'orange',
  'No-show': 'red',
  'Check-in': 'blue',
  'In-treatment': 'purple',
  'Check-out': 'cyan',
  Complete: 'vivid-green',
}

export const SCHEDULER_DEMO_COLUMNS: SchedulerColumn[] = [
  { id: 'room-1', label: 'Room No. 1' },
  { id: 'room-2', label: 'Room No. 2' },
  { id: 'room-3', label: 'Room No. 3' },
  { id: 'room-4', label: 'Room No. 4' },
  { id: 'room-5', label: 'Room No. 5' },
]

export const SCHEDULER_DEMO_PROVIDERS: SchedulerProviderOption[] = [
  { id: 'prov-james', name: 'Dr. James McArthur' },
  { id: 'prov-elena', name: 'Dr. Elena Ruiz' },
  { id: 'prov-sam', name: 'Dr. Sam Ortiz' },
  { id: 'prov-avery', name: 'Dr. Avery Mendel' },
  { id: 'prov-carlyn', name: 'Dr. Carlyn Greasley' },
]

export const SCHEDULER_DEMO_HIGHLIGHT_BANDS: SchedulerHighlightBand[] = [
  { id: 'focus-slot', startHour: 9.5, endHour: 9.75, tone: 'blue' },
  { id: 'lunch', startHour: 13, endHour: 14, tone: 'yellow' },
]

/** Closed before 9:00 and after 18:00 on the demo grid (8–21). */
export const SCHEDULER_DEMO_BLOCK_RANGES: SchedulerBlockRange[] = [
  { id: 'closed-morning', startHour: 8, endHour: 9 },
  { id: 'closed-evening', startHour: 18, endHour: 21 },
]

/** Demo day matching Scheduler (Jan 8, 2026). */
export const SCHEDULER_DEMO_DATE = new Date(2026, 0, 8, 0, 0, 0, 0)

/** Empty selection = show all (matches SearchWithFilter reset). */
export const createDefaultSchedulerFilters = (
  _columns: SchedulerColumn[] = SCHEDULER_DEMO_COLUMNS,
  _providers: SchedulerProviderOption[] = SCHEDULER_DEMO_PROVIDERS,
): SchedulerFilterState => ({
  providerIds: [],
  roomIds: [],
  statuses: [],
})

const demoInfo = (
  patientName: string,
  provider: string,
  dateTime: string,
  status: SchedulerApptStatus,
): SchedulerAppointmentInfo => ({
  patientName,
  provider,
  clinic: 'XYZ Clinic',
  procedure: 'D2200, D0140',
  dateTime,
  status,
  statusColor: SCHEDULER_STATUS_BADGE_COLOR[status],
  preferredDentist: provider.replace(/^Dr\.\s*/, ''),
  preferredHygienist: '-',
  isInsured: 'Yes (Verified)',
  conditions: '-',
  note: '-',
  preAuthStatus: 'Pending',
  preAuthColor: 'yellow',
})

type DemoSeed = {
  id: string
  dayOffset: number
  startHour: number
  startMinute: number
  durationHours: number
  columnId: string
  patientName: string
  appointmentType: string
  color: ScheduleCardColor
  providerId: string
  providerName: string
  status: SchedulerApptStatus
}

const DEMO_SEEDS: DemoSeed[] = [
  {
    id: 'evt-1',
    dayOffset: 0,
    startHour: 9,
    startMinute: 0,
    durationHours: 1.5,
    columnId: 'room-2',
    patientName: 'Sophie M. Markus',
    appointmentType: 'Periodontal (Prosthesis removal)',
    color: 'purple',
    providerId: 'prov-james',
    providerName: 'Dr. James McArthur',
    status: 'Confirmed',
  },
  {
    id: 'evt-2',
    dayOffset: 0,
    startHour: 11,
    startMinute: 0,
    durationHours: 2,
    columnId: 'room-3',
    patientName: 'Sophie M. Markus',
    appointmentType: 'Periodontal (Prosthesis removal)',
    color: 'pale-green',
    providerId: 'prov-james',
    providerName: 'Dr. James McArthur',
    status: 'Check-in',
  },
  {
    id: 'evt-3',
    dayOffset: 0,
    startHour: 12,
    startMinute: 0,
    durationHours: 1,
    columnId: 'room-4',
    patientName: 'Norman Redwood',
    appointmentType: 'Periodontal (Prosthesis removal)',
    color: 'yellow',
    providerId: 'prov-james',
    providerName: 'Dr. James McArthur',
    status: 'Unconfirmed',
  },
  {
    id: 'evt-4',
    dayOffset: -2,
    startHour: 10,
    startMinute: 0,
    durationHours: 1,
    columnId: 'room-1',
    patientName: 'Ava Chen',
    appointmentType: 'Cleaning',
    color: 'blue',
    providerId: 'prov-elena',
    providerName: 'Dr. Elena Ruiz',
    status: 'Confirmed',
  },
  {
    id: 'evt-5',
    dayOffset: -1,
    startHour: 14,
    startMinute: 0,
    durationHours: 1.5,
    columnId: 'room-2',
    patientName: 'Marcus Lee',
    appointmentType: 'Crown prep',
    color: 'orange',
    providerId: 'prov-elena',
    providerName: 'Dr. Elena Ruiz',
    status: 'In-treatment',
  },
  {
    id: 'evt-6',
    dayOffset: 1,
    startHour: 9,
    startMinute: 30,
    durationHours: 1,
    columnId: 'room-3',
    patientName: 'Priya Shah',
    appointmentType: 'Consultation',
    color: 'purple',
    providerId: 'prov-james',
    providerName: 'Dr. James McArthur',
    status: 'Late',
  },
  {
    id: 'evt-7',
    dayOffset: 2,
    startHour: 11,
    startMinute: 0,
    durationHours: 2,
    columnId: 'room-5',
    patientName: 'Owen Blake',
    appointmentType: 'Implant consult',
    color: 'pale-green',
    providerId: 'prov-sam',
    providerName: 'Dr. Sam Ortiz',
    status: 'Confirmed',
  },
  {
    id: 'evt-8',
    dayOffset: 3,
    startHour: 15,
    startMinute: 0,
    durationHours: 1,
    columnId: 'room-1',
    patientName: 'Hannah Brooks',
    appointmentType: 'Hygiene',
    color: 'yellow',
    providerId: 'prov-elena',
    providerName: 'Dr. Elena Ruiz',
    status: 'Check-out',
  },
  {
    id: 'evt-9',
    dayOffset: 5,
    startHour: 10,
    startMinute: 0,
    durationHours: 1,
    columnId: 'room-4',
    patientName: 'Leo Park',
    appointmentType: 'Emergency',
    color: 'red',
    providerId: 'prov-sam',
    providerName: 'Dr. Sam Ortiz',
    status: 'No-show',
  },
  {
    id: 'evt-10',
    dayOffset: 7,
    startHour: 10,
    startMinute: 0,
    durationHours: 1.5,
    columnId: 'room-2',
    patientName: 'Mia Torres',
    appointmentType: 'Whitening',
    color: 'blue',
    providerId: 'prov-james',
    providerName: 'Dr. James McArthur',
    status: 'Complete',
  },
  {
    id: 'evt-11',
    dayOffset: 10,
    startHour: 15,
    startMinute: 0,
    durationHours: 1,
    columnId: 'room-3',
    patientName: 'Noah Kim',
    appointmentType: 'Follow-up',
    color: 'pale-green',
    providerId: 'prov-sam',
    providerName: 'Dr. Sam Ortiz',
    status: 'Confirmed',
  },
  {
    id: 'evt-12',
    dayOffset: -5,
    startHour: 9,
    startMinute: 0,
    durationHours: 1,
    columnId: 'room-5',
    patientName: 'Grace Nguyen',
    appointmentType: 'Exam',
    color: 'orange',
    providerId: 'prov-elena',
    providerName: 'Dr. Elena Ruiz',
    status: 'Unconfirmed',
  },
  /* Dense week clusters — side-by-side lanes like Appointment WeekCalendar */
  {
    id: 'evt-w1',
    dayOffset: -1,
    startHour: 8,
    startMinute: 0,
    durationHours: 0.75,
    columnId: 'room-1',
    patientName: 'Emma Mitchell',
    appointmentType: 'Orthodontic Appt',
    color: 'blue',
    providerId: 'prov-elena',
    providerName: 'Dr. Elena Ruiz',
    status: 'Confirmed',
  },
  {
    id: 'evt-w2',
    dayOffset: -1,
    startHour: 8,
    startMinute: 0,
    durationHours: 0.75,
    columnId: 'room-2',
    patientName: 'Dana Lyddy',
    appointmentType: 'Oral Surgery Appt',
    color: 'pale-green',
    providerId: 'prov-james',
    providerName: 'Dr. James McArthur',
    status: 'Check-in',
  },
  {
    id: 'evt-w3',
    dayOffset: -1,
    startHour: 8,
    startMinute: 15,
    durationHours: 0.5,
    columnId: 'room-3',
    patientName: 'Chris Vale',
    appointmentType: 'Cleaning',
    color: 'red',
    providerId: 'prov-sam',
    providerName: 'Dr. Sam Ortiz',
    status: 'Unconfirmed',
  },
  {
    id: 'evt-w4',
    dayOffset: -1,
    startHour: 8,
    startMinute: 0,
    durationHours: 0.75,
    columnId: 'room-4',
    patientName: 'Rita Gomez',
    appointmentType: 'Hygiene',
    color: 'yellow',
    providerId: 'prov-elena',
    providerName: 'Dr. Elena Ruiz',
    status: 'Confirmed',
  },
  {
    id: 'evt-w5',
    dayOffset: -1,
    startHour: 8,
    startMinute: 0,
    durationHours: 0.5,
    columnId: 'room-5',
    patientName: 'Jon Park',
    appointmentType: 'Consult',
    color: 'purple',
    providerId: 'prov-james',
    providerName: 'Dr. James McArthur',
    status: 'Late',
  },
  {
    id: 'evt-w6',
    dayOffset: -1,
    startHour: 10,
    startMinute: 0,
    durationHours: 1,
    columnId: 'room-1',
    patientName: 'Emma Mitchell',
    appointmentType: 'Orthodontic Appt',
    color: 'blue',
    providerId: 'prov-elena',
    providerName: 'Dr. Elena Ruiz',
    status: 'In-treatment',
  },
  {
    id: 'evt-w7',
    dayOffset: -1,
    startHour: 10,
    startMinute: 0,
    durationHours: 1,
    columnId: 'room-2',
    patientName: 'Dana Lyddy',
    appointmentType: 'Oral Surgery Appt',
    color: 'purple',
    providerId: 'prov-james',
    providerName: 'Dr. James McArthur',
    status: 'Confirmed',
  },
  {
    id: 'evt-w8',
    dayOffset: 0,
    startHour: 10,
    startMinute: 0,
    durationHours: 1.5,
    columnId: 'room-2',
    patientName: 'Jane Doe',
    appointmentType: 'Cosmetic Appointment',
    color: 'pale-green',
    providerId: 'prov-sam',
    providerName: 'Dr. Sam Ortiz',
    status: 'Confirmed',
  },
  {
    id: 'evt-w9',
    dayOffset: 1,
    startHour: 9,
    startMinute: 0,
    durationHours: 0.75,
    columnId: 'room-1',
    patientName: 'Alex Rivera',
    appointmentType: 'Exam',
    color: 'blue',
    providerId: 'prov-elena',
    providerName: 'Dr. Elena Ruiz',
    status: 'Confirmed',
  },
  {
    id: 'evt-w10',
    dayOffset: 1,
    startHour: 9,
    startMinute: 15,
    durationHours: 0.75,
    columnId: 'room-3',
    patientName: 'Sam Ortiz Jr',
    appointmentType: 'Follow-up',
    color: 'orange',
    providerId: 'prov-sam',
    providerName: 'Dr. Sam Ortiz',
    status: 'Check-in',
  },
  /* Extra providers on demo day so Month “+ more” has overflow */
  {
    id: 'evt-m1',
    dayOffset: 0,
    startHour: 9,
    startMinute: 0,
    durationHours: 1,
    columnId: 'room-1',
    patientName: 'Quinn Adler',
    appointmentType: 'Exam',
    color: 'blue',
    providerId: 'prov-avery',
    providerName: 'Dr. Avery Mendel',
    status: 'Confirmed',
  },
  {
    id: 'evt-m2',
    dayOffset: 0,
    startHour: 11,
    startMinute: 0,
    durationHours: 1,
    columnId: 'room-5',
    patientName: 'Blake Foster',
    appointmentType: 'Cleaning',
    color: 'red',
    providerId: 'prov-carlyn',
    providerName: 'Dr. Carlyn Greasley',
    status: 'Check-in',
  },
  {
    id: 'evt-m3',
    dayOffset: 0,
    startHour: 14,
    startMinute: 0,
    durationHours: 1,
    columnId: 'room-4',
    patientName: 'Casey Wong',
    appointmentType: 'Consult',
    color: 'purple',
    providerId: 'prov-elena',
    providerName: 'Dr. Elena Ruiz',
    status: 'Confirmed',
  },
]

/**
 * Demo events scattered around `baseDate` so Day / Week / Month all have content.
 */
export const createSchedulerDemoEvents = (
  baseDate: Date = SCHEDULER_DEMO_DATE,
): SchedulerEvent[] => {
  const base = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth(),
    baseDate.getDate(),
  )

  return DEMO_SEEDS.map((seed) => {
    const day = new Date(base)
    day.setDate(day.getDate() + seed.dayOffset)
    const start = new Date(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      seed.startHour,
      seed.startMinute,
    )
    const end = new Date(start.getTime() + seed.durationHours * 60 * 60 * 1000)
    const dateTime = start.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })

    return {
      id: seed.id,
      columnId: seed.columnId,
      start,
      end,
      patientName: seed.patientName,
      appointmentType: seed.appointmentType,
      color: seed.color,
      providerId: seed.providerId,
      providerName: seed.providerName,
      status: seed.status,
      info: demoInfo(
        seed.patientName,
        seed.providerName,
        dateTime,
        seed.status,
      ),
    }
  })
}
